<template>
  <div class="divide-y">
    <CollapsePanel :shouldListenForOpenCommand="true">
      <template slot="title">
        <div v-t="'data'"></div>
      </template>
      <div>
        <CalendarDataChooser :widgetId="model.wid" />
      </div>
    </CollapsePanel>

    <CollapsePanel>
      <template slot="title">
        <div v-t="'calendarDay.dayTitle'"></div>
      </template>

      <div class="pb-3 px-4">
        <div
          class="text-sm text-gray-500 ml-2"
          v-t="'calendarDay.dateFormat'"
        ></div>
        <EditorSelectMenu
          :value="model.dayFormat"
          :options="dayFormatOptions"
          @input="apply({ dayFormat: $event })"
        />
      </div>
      <div class="pb-3 px-4">
        <div
          class="text-sm text-gray-500 ml-2"
          v-t="'calendarDay.dateOffset'"
        ></div>
        <EditorSelectMenu
          :value="model.dayOffset"
          :options="offsetOptions"
          @input="apply({ dayOffset: $event })"
        />
        <div
          class="text-sm text-gray-600 mt-2 px-2 py-1 leading-tight border rounded-sm shadow-sm"
          v-t="'calendarDay.offsetExplain'"
        ></div>
      </div>
      <GroupTextFormatEditor
        :model="model"
        element="day"
        @update="apply($event)"
        :disableLineHeight="true"
      />

      <ToggleInput class="px-4" :value="model.syncFonts" @input="syncFontsInput"
        ><span class="text-sm" v-t="'syncFonts'"></span
      ></ToggleInput>

      <ToggleInput
        class="pt-2 pb-5 px-4"
        :value="model.syncTextColors"
        @input="syncTextColorsInput"
        ><span class="text-sm" v-t="'syncTextColors'"></span
      ></ToggleInput>
    </CollapsePanel>

    <CollapsePanel>
      <template slot="title">
        <div v-t="'calendarDay.hourTitle'"></div>
      </template>
      <div>
        <GroupTextFormatEditor
          :model="model"
          element="hours"
          @update="apply($event)"
          :disableAlignment="true"
          :disableLineHeight="true"
        />
        <ColorInputBar
          class="py-3 px-4"
          :value="model.verticalLinesColor"
          @input="preview({ verticalLinesColor: $event })"
          @change="apply({ verticalLinesColor: $event })"
          propName="verticalLinesColor"
        >
          <span class="text-sm" v-t="'calendarDay.lineColor'"></span>
        </ColorInputBar>
        <!-- hi -->
        <ToggleInput
          class="py-3 px-4"
          :value="use24Hour"
          @input="apply({ use24Hour: $event })"
          ><span class="text-sm" v-t="'calendarDay.use24HourClock'"></span
        ></ToggleInput>

        <div class="pb-5 px-7">
          <div
            class="text-sm text-gray-500"
            v-t="'calendarDay.hourRange'"
          ></div>
          <DoubleSliderInput
            :min="0"
            :max="24"
            :minRange="3"
            :minValue="model.startHour"
            :maxValue="model.endHour"
            @updateMin="updateStartHour"
            @updateMax="updateEndHour"
          />

          <div class="flex justify-center">
            <div
              class="inline-block text-center text-xs px-3 py-1 bg-gray-100 border rounded-lg"
            >
              {{ formattedHourRange }}
            </div>
          </div>
        </div>
      </div>
    </CollapsePanel>

    <CollapsePanel>
      <template slot="title">
        <div v-t="'calendarDay.timeMarker'"></div>
      </template>
      <div class="pb-5 pt-3 pl-6">
        <ScalarBinder
          propName="nowLineColor"
          propType="Color"
          v-slot:default="slotProps"
          tooltipPosition="r"
        >
          <ColorInput
            @input="preview({ nowLineColor: $event })"
            @change="apply({ nowLineColor: $event })"
            :value="slotProps.value"
            :dataBound="slotProps.isBound"
          />
        </ScalarBinder>
      </div>
    </CollapsePanel>

    <CollapsePanel>
      <template slot="title">
        <div v-t="'calendarDay.calendarBackground'"></div>
      </template>
      <div class="pb-5 pt-3 pl-6">
        <ScalarBinder
          propName="calendarBackgroundColor"
          propType="Color"
          v-slot:default="slotProps"
          tooltipPosition="r"
        >
          <ColorInput
            :value="slotProps.value"
            :dataBound="slotProps.isBound"
            @input="preview({ calendarBackgroundColor: $event })"
            @change="apply({ calendarBackgroundColor: $event })"
        /></ScalarBinder>
      </div>
    </CollapsePanel>

    <CollapsePanel>
      <template slot="title">
        <div v-t="'calendarDay.eventBackground'"></div>
      </template>
      <div class="pb-5 pt-3 pl-6">
        <ScalarBinder
          propName="eventBackgroundColor"
          propType="Color"
          v-slot:default="slotProps"
          tooltipPosition="r"
        >
          <ColorInput
            :value="slotProps.value"
            :dataBound="slotProps.isBound"
            @input="preview({ eventBackgroundColor: $event })"
            @change="apply({ eventBackgroundColor: $event })"
        /></ScalarBinder>
      </div>
    </CollapsePanel>

    <CollapsePanel>
      <template slot="title">
        <div v-t="'calendarDay.eventTitle'"></div>
      </template>
      <div class="pb-3">
        <GroupTextFormatEditor
          :model="model"
          element="eventTitle"
          @update="apply($event)"
        />
      </div>
    </CollapsePanel>

    <CollapsePanel>
      <template slot="title">
        <div v-t="'calendarDay.eventLocation'"></div>
      </template>
      <div>
        <GroupTextFormatEditor
          :model="model"
          element="eventLocation"
          @update="apply($event)"
        />
        <button
          class="text-sm flex space-x-2 pl-6 pb-5"
          @click="apply({ showEventLocation: !showEventLocation })"
        >
          <Icon :name="eventLocationEyeIcon" class="w-5 h-5" />
          <div v-t="eventLocationVisibility"></div>
        </button>
      </div>
    </CollapsePanel>

    <CollapsePanel>
      <template slot="title">
        <div v-t="'calendarDay.eventTime'"></div>
      </template>
      <div>
        <GroupTextFormatEditor
          :model="model"
          element="eventTime"
          @update="apply($event)"
        />
        <button
          class="text-sm flex space-x-2 pl-6 pb-5"
          @click="apply({ showEventTime: !showEventTime })"
        >
          <Icon :name="eventTimeEyeIcon" class="w-5 h-5" />
          <div v-t="eventTimeVisibility"></div>
        </button>
      </div>
    </CollapsePanel>
    <BaseWidgetEditor :selectionLocked="model.locked" />
  </div>
</template>

<script lang="ts">
import { Component, Vue } from "vue-property-decorator";
import { DateTime } from "luxon";
import Icon from "@/components/icons/Icon.vue";
import CalendarDataChooser from "@/components/widgets/CalendarDataChooser.vue";
import CollapsePanel from "@/components/CollapsePanel.vue";
import ColorInput from "@/components/inputs/ColorInput.vue";
import ColorInputBar from "@/components/inputs/ColorInputBar.vue";
import FontFamilyInput from "@/components/inputs/FontFamilyInput.vue";
import FormLabel from "@/components/FormLabel.vue";
import GroupTextFormatEditor from "@/components/editors/GroupTextFormatEditor.vue";
import EditorSelectMenu from "@/components/EditorSelectMenu.vue";
import TextInput from "@/components/inputs/TextInput.vue";
import ToggleInput from "@/components/inputs/ToggleInput.vue";
import DoubleSliderInput from "@/components/inputs/DoubleSliderInput.vue";
import BaseWidgetEditor from "@/components/BaseWidgetEditor.vue";
import { CalendarDayOptions } from "./CalendarDayOptions";
import ScalarBinder from "@/components/editors/ScalarBinder.vue";
import { useAppEditorStore } from "@/stores/appEditor";

@Component({
  components: {
    CollapsePanel,
    TextInput,
    ToggleInput,
    EditorSelectMenu,
    FormLabel,
    Icon,
    FontFamilyInput,
    ColorInput,
    ColorInputBar,
    CalendarDataChooser,
    GroupTextFormatEditor,
    DoubleSliderInput,
    BaseWidgetEditor,
    ScalarBinder,
  },
})
export default class CalendarDayEditor extends Vue {
  get appEditor() {
    return useAppEditorStore();
  }

  get model() {
    return this.appEditor.selectedWidget as unknown as CalendarDayOptions;
  }

  updateStartHour(ev: any) {
    const { value, track } = ev;
    this.apply({ startHour: value, track });
  }

  updateEndHour(ev: any) {
    const { value, track } = ev;
    this.apply({ endHour: value, track });
  }

  apply(props: any) {
    this.appEditor.setWidgetProps([this.model.wid], props);
  }

  preview(props: any) {
    this.appEditor.setWidgetProps([this.model.wid], props, "NO_UNDO");
  }

  get textElements() {
    return ["day", "hours", "eventTitle", "eventTime", "eventLocation"];
  }

  syncFontsInput(val: boolean) {
    this.appEditor.toggleSyncFonts({
      wid: this.model.wid,
      syncFonts: val,
      textElements: this.textElements,
    });
  }

  syncTextColorsInput(val: boolean) {
    this.appEditor.toggleSyncTextColors({
      wid: this.model.wid,
      syncTextColors: val,
      textElements: this.textElements,
    });
  }

  // TODO: Perhaps want to change these based on dayoffset
  get dayFormatOptions() {
    const d = new Date();
    return [
      {
        label: "Today",
        value: "today",
      },
      {
        label: d.toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
        }),
        value: "date-short",
      },
      {
        label: d.toLocaleDateString("en-US", {
          month: "short",
          weekday: "short",
          day: "numeric",
        }),
        value: "date-med",
      },
      {
        label: d.toLocaleDateString("en-US", {
          month: "long",
          weekday: "long",
          day: "numeric",
        }),
        value: "date-long",
      },
    ];
  }

  get offsetOptions() {
    return [
      { label: this.$t("calendarOffsetLabel.yesterday"), value: -1 },
      { label: this.$t("calendarOffsetLabel.currentDay"), value: 0 },
      { label: this.$t("calendarOffsetLabel.oneDayAhead"), value: 1 },
      { label: this.$t("calendarOffsetLabel.twoDaysAhead"), value: 2 },
      { label: this.$t("calendarOffsetLabel.threeDaysAhead"), value: 3 },
    ];
  }

  get use24Hour() {
    return this.model.use24Hour;
  }

  get eventLocationEyeIcon() {
    if (this.model.showEventLocation) {
      return "Eye";
    } else {
      return "EyeOff";
    }
  }

  get eventLocationVisibility() {
    if (this.model.showEventLocation) {
      return "visible";
    } else {
      return "hidden";
    }
  }

  get eventTimeEyeIcon() {
    if (this.model.showEventLocation) {
      return "Eye";
    } else {
      return "EyeOff";
    }
  }

  get eventTimeVisibility() {
    if (this.model.showEventLocation) {
      return "visible";
    } else {
      return "hidden";
    }
  }

  get showEventLocation() {
    return this.model.showEventLocation;
  }

  set showEventLocation(value: any) {
    this.apply({ showEventLocation: value });
  }

  get showEventTime() {
    return this.model.showEventTime;
  }

  set showEventTime(value: any) {
    this.apply({ showEventTime: value });
  }

  // --------------------------------------------------------
  // Start / End Hours
  // --------------------------------------------------------

  get formattedHourRange() {
    return this.$t("calendarDay.hourRangeValues", {
      start: this.formatHour(this.model.startHour),
      end: this.formatHour(this.model.endHour),
    });
  }

  formatHour(hour: number) {
    return DateTime.fromObject({ hour: hour, minute: 0 }).toLocaleString({
      hour: "numeric",
      hour12: !this.use24Hour,
    });
  }
}
</script>
